/**
 * Generic pagination grid component for loading more items via Shopify's Section Rendering API
 *
 * Required markup structure:
 * ```html
 * <pagination-grid>
 *   {% paginate collection.products by 12 %}
 *     <div data-pagination-grid data-next-url="{{ paginate.next.url }}">
 *       <div data-pagination-list>
 *         {% for product in collection.products %}
 *           <div data-pagination-grid-item>
 *             <!-- Item content -->
 *           </div>
 *         {% endfor %}
 *       </div>
 *
 *       <!-- Optional pagination count -->
 *       <div data-paginate-count class="flex flex-col items-center">
 *         <span class="text-brand-01 mb-2xs">
 *           {{-
 *             'general.pagination.current_count'
 *             | t: count: pagination_current_item_end, total: paginate.items
 *           -}}
 *         </span>
 *         <div class="w-[160px] h-[2px] my-2xs bg-neutral relative">
 *           <div class="absolute inset-0 h-[2px] bg-brand-01" style="width: {{ progress }}%;"></div>
 *         </div>
 *       </div>
 *
 *       <!-- Load more button -->
 *       {% if paginate.next %}
 *         <button type="button" data-load-more>Load more</button>
 *         <div
 *           class="hidden w-[25px] h-[25px] ml-auto mr-auto border-[3px] border-solid border-brand-01 border-t-neutral rounded-full animate-spin"
 *           data-load-more-spinner
 *         ></div>
 *       {% endif %}
 *     </div>
 *   {% endpaginate %}
 * </pagination-grid>
 * ```
 */

if (!customElements.get('pagination-grid')) {
  customElements.define(
    'pagination-grid',
    class PaginationGrid extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          paginationGrid: '[data-pagination-grid]',
          paginationList: '[data-pagination-list]',
          paginationItem: '[data-pagination-grid-item]',
          loadMoreButton: '[data-load-more]',
          loadMoreSpinner: '[data-load-more-spinner]',
          paginateCount: '[data-paginate-count]',
        };

        this.focasables = getFocusableElements(this);
        this.initLoadMore();
      }

      initLoadMore() {
        this.addEventListener('click', (event) => {
          if (event.target.hasAttribute('data-load-more')) {
            const nextUrl = this.querySelector(this.selectors.paginationGrid)?.dataset?.nextUrl;
            if (nextUrl) {
              this.loadMoreItems(nextUrl);
            }
          }
        });
      }

      loadMoreItems(url) {
        const paginationGrid = this.querySelector(this.selectors.paginationGrid);
        const loadMoreButton = this.querySelector(this.selectors.loadMoreButton);
        const loadMoreSpinner = this.querySelector(this.selectors.loadMoreSpinner);
        const paginateCount = this.querySelector(this.selectors.paginateCount);
        loadMoreButton.classList.add('hidden');
        loadMoreSpinner.classList.remove('hidden');

        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            loadMoreButton.classList.remove('hidden');
            loadMoreSpinner.classList.add('hidden');

            const paginationGridNew = parsedHTML.querySelector(this.selectors.paginationGrid);
            const paginateCountNew = parsedHTML.querySelector(this.selectors.paginateCount);
            if (paginateCount && paginateCountNew) {
              paginateCount.innerHTML = paginateCountNew.innerHTML;
            }

            const paginationList = this.querySelector(this.selectors.paginationList);
            const nextItems = paginationGridNew.querySelectorAll(this.selectors.paginationItem);

            const newUrl = paginationGridNew.dataset.nextUrl;
            paginationGrid.setAttribute('data-next-url', newUrl);

            if (newUrl) {
              loadMoreButton.classList.remove('hidden');
            } else {
              loadMoreButton.parentElement.classList.add('hidden');
            }

            nextItems.forEach((item) => {
              paginationList.appendChild(item);
            });

            // Update focus state to first added focasable element
            const previousFocusIndex = [...this.focasables].indexOf(loadMoreButton);

            if (previousFocusIndex) {
              this.focasables = getFocusableElements(this);
              this.focasables[previousFocusIndex].focus();
            }
          });
      }
    }
  );
}
